export type ProspectType = "" | "PERSONNE_PHYSIQUE" | "PERSONNE_MORALE";

// Canal d'entree en relation. Miroir de l'enumeration OnboardingChannel du back :
// DIGITAL (canal digital sans passage en agence), AGENCY (cree en agence avec verification
// biometrique), CBS_IMPORT (repris du core banking).
export type CanalType = "" | "DIGITAL" | "AGENCY" | "CBS_IMPORT";

export type ScreeningStatus = "" | "NON_SCREENÉ" | "SCREENÉ";

export type ProspectStatus = "" | "PROSPECT" | "CLIENT" | "BLOQUÉ";

// Statuts que l'ecran prospects accepte de filtrer. Sous-ensemble de l'enumeration CustomerStatus
// du back, restreint par GET /customers/prospects : tout autre statut est refuse en 400, sinon la
// liste deviendrait un doublon de la liste clients.
// Sans filtre, le back applique son defaut - PROSPECT seul, le perimetre historique de l'ecran.
export type ProspectScreenStatus = "PROSPECT" | "PENDING_SCREENING" | "REFUSED";
export const CREDENTIAL_STATUS = {
  ACTIVE: "Accès actif",
  EXPIRED: "Accès expiré",
  REVOKED: "Accès révoqué",
  ARCHIVED: "Accès archivé",
  REVOCATION_IN_PROGRESS: "Révocation en cours",
  NOT_GENERATED: "Non généré",
} as const;

export type CredentialStatus =
  (typeof CREDENTIAL_STATUS)[keyof typeof CREDENTIAL_STATUS];

export type ProductOfInterest = "" | "CRÉDIT" | "ÉPARGNE" | "CARTE" | "XOF";

export type ProspectTimeline = {
  type: string;
  date: string;
  status: ScreeningStatus | ProspectStatus;
};

/** Shared type for API/domain code+name pairs (e.g. nationality, civility). */
export interface CodeName {
  code: string;
  name: string;
}

export interface Prospect {
  id: string;
  business_key: string;
  type?: ProspectType;
  first_name: string;
  last_name: string;
  /** Raison sociale (personne morale uniquement). Prime sur first_name/last_name pour l'affichage. */
  companyName?: string | null;
  phone?: string | null;
  phone1?: string | null;
  phone1CountryCode?: string | null;
  phone1Verified?: boolean;
  phone2?: string | null;
  phone2CountryCode?: string | null;
  phone2Verified?: boolean;
  phone3?: string | null;
  phone3CountryCode?: string | null;
  phone3Verified?: boolean;
  phone4?: string | null;
  phone4CountryCode?: string | null;
  phone4Verified?: boolean;
  fax?: string | null;
  // ProspectStatus (PROSPECT/CLIENT/BLOQUÉ) ne couvrait que l'ecran legacy : la liste demande
  // desormais aussi PENDING_SCREENING et REFUSED (voir ProspectScreenStatus), donc le champ doit
  // pouvoir porter l'une ou l'autre valeur reellement renvoyee par le back.
  status?: ProspectStatus | ProspectScreenStatus;
  productOfInterest?: string | null;
  products?: string[];
  email?: string | null;
  email1?: string | null;
  email1Verified?: boolean;
  email2?: string | null;
  email2Verified?: boolean;
  civilityCode?: string | null;
  civility?: CodeName | null;
  gender?: string | null;
  birthDate?: string | null;
  birthPlace?: string | null;
  nationalityCode?: string | null;
  nationality?: CodeName | null;
  address?: string | null;
  city?: string | null;
  postalCode?: string | null;
  countryResidenceCode?: string | null;
  countryResidence?: CodeName | null;
  familyStatusCode?: string | null;
  familyStatus?: CodeName | null;
  professionCategory?: CodeName | null;
  professionCode?: string | null;
  profession?: string | null;
  professionCountry?: CodeName | null;
  monthlyIncome?: number;
  sectorActivity?: string | null;
  qualityCode?: string | null;
  peps?: boolean;
  taxIdNumber?: string | null;
  resident?: boolean;
  numberChildren?: number;
  comment?: string;
  contactForSavingCardCredit?: boolean;
  mounthlyIncome?: number;
  issueDate?: string;
  expireDate?: string;
  issueCountry?: string;
  documentNumber?: string;
  createdTimestamp?: string | null;
  branch?: string;
  advisor?: string;
  countryOfBirth?: string;
  spouseFirstName?: string | null;
  spouseLastName?: string | null;
  otherBankRelation?: boolean | null;
  bankPersonnel?: boolean | null;
  syncStatus?: string | null;
  syncErrorMessage?: string | null;
  lastCbsSyncAt?: string | null;
  credentialStatus?: CredentialStatus;
  onboardingChannel?: CanalType | null;
  partnerFirstName?: string;
  partnerLastName?: string;
  fatherLastName?: string;
  fatherFirstName?: string;
  motherMaidenName?: string;
  motherFirstName?: string;
}

/** Identité légale de l'entreprise, portée par le prospect PM en plus du déclarant. */
export interface ProspectLegalEntity {
  companyName?: string | null;
  legalFormCode?: string | null;
  /** Libellé de la forme juridique, résolu par le back (ex. "GRPT.INTERET ECONOMIQUE (G.I.E.)"). */
  legalFormLabel?: string | null;
  incorporationCountryCode?: string | null;
  /** Libellé du pays d'incorporation, résolu par le back (ex. "COTE D'IVOIRE"). */
  incorporationCountryName?: string | null;
  rccm?: string | null;
  /** Détails d'entreprise de l'écran 02 (#26005) — saisis et persistés progressivement après la création. */
  sectorActivityCode?: string | null;
  sectorActivityLabel?: string | null;
  shareCapital?: number | null;
  incorporationDate?: string | null;
  headOfficeAddress?: string | null;
  corporatePurpose?: string | null;
  tradeName?: string | null;
  city?: string | null;
}

export interface ProspectDetails {
  id: string;
  businessKey: string;
  firstName: string;
  lastName: string;
  type: ProspectType;
  /** Identité de l'entreprise (personne morale uniquement) — firstName/lastName restent le déclarant. */
  legalEntity?: ProspectLegalEntity | null;
  email?: string;
  phone: string;
  phoneCountryCode?: string | null;
  phoneVerified: boolean;
  country: string;
  canal?: CanalType;
  screening?: ScreeningStatus;
  status: ProspectStatus;
  lastUpdate: string;
  onboarding: boolean;
  notes?: string;
  motif?: string;
  birthDate: string;
  birthPlace?: string;
  IdDocType?: "PASSPORT" | "CNI" | null;
  IdDocNumber?: string;
  expiryDate?: string;
  deliveredDate?: string;
  address1?: string;
  address2?: string;
  city?: string;
  timeline: ProspectTimeline[];
  nationality?: {
    id?: number | null;
    code: string;
    label: string;
  };
  gender?: string;
  postalCode?: string;
  contryOfApplication?: {
    id: number | null;
    code: string;
    label: string;
  };
  comment?: string;
  contactForSavingCardCredit?: boolean;
  mounthlyIncome?: number;
  peps?: boolean;
  productsOfInterest?: string[];
  credentialStatus?: CredentialStatus;
}

export interface ProspectsResponse {
  code: string;
  message: string;
  data: {
    items: Prospect[];
    totalElements: number;
  };
}

export interface ProspectDetailsResponse {
  code: string;
  message: string;
  data: ProspectDetails;
}

/**
 * Criteres de recherche de l'ecran prospects, tels que la vue les tient.
 *
 * Regroupes en un objet plutot qu'enumeres en arguments : la recherche en comptait deja quatre et
 * la periode, le statut et le canal en auraient fait huit, dont plusieurs chaines de caracteres que
 * seul leur rang aurait distinguees.
 */
export type ProspectSearchFilters = {
  name?: string;
  type?: ProspectType;
  /** Vide = perimetre historique de l'ecran, c'est-a-dire les prospects seuls. */
  status?: ProspectScreenStatus[];
  canal?: CanalType;
  /** Bornes incluses, au format AAAA-MM-JJ. */
  createdFrom?: string;
  createdTo?: string;
  page?: number;
  size?: number;
  /** Champ de tri serveur : `last_name` ou `createdTimestamp`. Absent = tri par défaut (plus récent en tête). */
  sortBy?: "last_name" | "createdTimestamp";
  sortDirection?: "asc" | "desc";
};

/**
 * Parametres de requete de GET /customers/prospects, nommes comme le back les lit.
 *
 * `status` est repetable (`?status=PROSPECT&status=REFUSED`) ; omis, le back applique son defaut.
 * `createdFrom` / `createdTo` sont des bornes incluses au format AAAA-MM-JJ.
 */
export type GetProspectsQueryParams = {
  filterValue?: string;
  customerType?: string;
  status?: ProspectScreenStatus[];
  channel?: CanalType;
  createdFrom?: string;
  createdTo?: string;
  offset?: string | number;
  rows?: string | number;
  sortBy?: "last_name" | "createdTimestamp";
  sortDirection?: "asc" | "desc";
};
